import React, { useEffect, useState, useRef } from 'react';
import { API_BASE_URL } from '../config';

function VerifyEmail() {
  const [email, setEmail]   = useState('');
  const [intent, setIntent] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [otp, setOtp]       = useState(['', '', '', '', '', '']);
  const [status, setStatus] = useState('idle'); // idle, verifying, success, error
  const [message, setMessage] = useState('');

  // Resend OTP Cooldown
  const [cooldown, setCooldown]   = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputsRef = useRef([]);

  useEffect(() => {
    const params    = new URLSearchParams(window.location.hash.split('?')[1]);
    const urlEmail  = params.get('email');
    const urlIntent = params.get('intent') || 'verify';
    const remember  = params.get('remember') === '1';

    if (urlEmail) setEmail(urlEmail);
    setIntent(urlIntent);
    setRememberDevice(remember);

    if (!urlEmail) {
      setStatus('error');
      setMessage('Missing email address. Please restart the process.');
    }
  }, []);

  useEffect(() => {
    let timer;
    if (cooldown > 0 && !canResend) {
      timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    } else if (cooldown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [cooldown, canResend]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputsRef.current[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) newOtp[i] = pastedData[i];
      setOtp(newOtp);
      const focusIndex = pastedData.length < 6 ? pastedData.length : 5;
      inputsRef.current[focusIndex].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = otp.join('');
    if (token.length !== 6) {
      setStatus('error');
      setMessage('Please enter all 6 digits.');
      return;
    }

    setStatus('verifying');
    setMessage('');

    try {
      const isLoginIntent = intent === 'login' || intent.startsWith('login');
      const endpoint = isLoginIntent ? '/api/auth/verify-login-otp' : '/api/auth/verify-email';

      const body = { email, token };
      if (isLoginIntent) body.rememberDevice = rememberDevice;

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message);

        if (isLoginIntent && data.token) {
          localStorage.setItem('dms_auth_token', data.token);

          // Persist trusted device token if the backend issued one
          if (data.trustedDeviceToken) {
            localStorage.setItem('dms_trusted_device_token', data.trustedDeviceToken);
            localStorage.setItem('dms_trusted_device_email', email);
          }

          window.dispatchEvent(new Event('dms-auth-updated'));
          setTimeout(() => { window.location.href = '#/dms/dashboard'; }, 1500);
        }
      } else {
        setStatus('error');
        setMessage(data.message || 'Verification failed.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Network error during verification.');
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCanResend(false);
    setCooldown(60);
    setStatus('idle');
    setMessage('');

    const isLoginIntent = intent === 'login' || intent.startsWith('login');

    try {
      const endpoint    = isLoginIntent ? '/api/auth/send-login-otp' : '/api/auth/resend-verification';
      const bodyPayload = { email };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload)
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('A new code has been sent to your email.');
      } else {
        setMessage(data.message || 'Failed to resend code.');
      }
    } catch (err) {
      setMessage('Network error resending code.');
    }
  };

  const isLoginIntent = intent === 'login' || intent.startsWith('login');

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg-dark, #0d1117)', color: '#c9d1d9' }}>
      <div style={{ background: '#161b22', padding: '40px', borderRadius: '10px', textAlign: 'center', border: '1px solid #30363d', maxWidth: '450px', width: '100%' }}>
        <h2>{isLoginIntent ? 'Login Verification' : 'Email Verification'}</h2>
        <p style={{ color: '#8b949e', marginBottom: '20px' }}>
          Enter the 6-digit code sent to <br /><strong>{email}</strong>
        </p>

        {status === 'success' ? (
          <>
            <p style={{ color: '#3fb950', marginTop: '15px' }}>{message}</p>
            {!isLoginIntent && (
              <button
                onClick={() => window.location.href = '#/'}
                style={{ marginTop: '20px', padding: '10px 20px', background: '#238636', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
              >
                Go to Login
              </button>
            )}
          </>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }} onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  style={{
                    width: '40px', height: '50px', fontSize: '24px',
                    textAlign: 'center', borderRadius: '5px',
                    border: '1px solid #30363d', background: '#0d1117', color: '#fff',
                  }}
                />
              ))}
            </div>

            {/* Remember this device — only shown for login OTP */}
            {isLoginIntent && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '13px', color: '#8b949e', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  style={{ accentColor: '#10b981', width: '16px', height: '16px' }}
                />
                Remember this device for 30 days
              </label>
            )}

            {status === 'error'     && <p style={{ color: '#f85149', marginBottom: '15px' }}>{message}</p>}
            {status === 'verifying' && <p style={{ color: '#58a6ff', marginBottom: '15px' }}>Verifying…</p>}
            {status === 'idle' && message && <p style={{ color: '#3fb950', marginBottom: '15px' }}>{message}</p>}

            <button
              type="submit"
              disabled={status === 'verifying'}
              style={{ width: '100%', padding: '12px', background: '#238636', color: '#fff', border: 'none', borderRadius: '5px', cursor: status === 'verifying' ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: 'bold' }}
            >
              Verify Code
            </button>
          </form>
        )}

        {status !== 'success' && (
          <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
            <a href="#/" style={{ color: '#58a6ff', textDecoration: 'none' }}>Back to Login</a>
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              style={{
                background: 'transparent', border: 'none',
                color: canResend ? '#58a6ff' : '#8b949e',
                cursor: canResend ? 'pointer' : 'not-allowed',
                textDecoration: canResend ? 'underline' : 'none'
              }}
            >
              {canResend ? 'Resend Code' : `Resend in ${cooldown}s`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
